/**
 * Product Gallery Module
 * Handles image gallery with thumbnails, prev/next navigation, zoom, and lightbox
 */
export class ProductGallery {
    constructor(container) {
        this.currentIndex = 0;
        this.videoIndex = -1;
        this.videoLoaded = false;
        this.startGalleryVideo = null;
        this.stopGalleryVideo = null;
        this.container = container;
        this.slider = container.querySelector('[data-gallery-main]');
        this.slides = container.querySelectorAll('.pdp-gallery__slide');
        this.thumbnails = container.querySelectorAll('[data-thumbnail]');
        this.prevBtn = container.querySelector('[data-gallery-prev]');
        this.nextBtn = container.querySelector('[data-gallery-next]');
        this.zoomContainer = container.querySelector('[data-zoom-container]');
        this.counter = container.querySelector('[data-gallery-counter]');
        this.videoSlide = container.querySelector('[data-video-slide]');
        if (this.videoSlide) {
            this.videoIndex = Array.prototype.indexOf.call(this.slides, this.videoSlide);
        }
        this.init();
        this.initThumbOverflowChip();
    }
    init() {
        this.initThumbnails();
        this.initNavigation();
        this.initZoom();
        this.initLightbox();
        this.initGalleryVideo();
    }
    initThumbnails() {
        this.thumbnails.forEach((thumb, index) => {
            thumb.addEventListener('click', () => {
                this.goToSlide(index);
            });
        });
    }
    initNavigation() {
        if (this.prevBtn) {
            this.prevBtn.addEventListener('click', () => this.prev());
        }
        if (this.nextBtn) {
            this.nextBtn.addEventListener('click', () => this.next());
        }
        // Keyboard navigation
        this.container.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') {
                this.prev();
            }
            else if (e.key === 'ArrowRight') {
                this.next();
            }
        });
    }
    prev() {
        const newIndex = this.currentIndex > 0
            ? this.currentIndex - 1
            : this.slides.length - 1;
        this.goToSlide(newIndex);
    }
    next() {
        const newIndex = this.currentIndex < this.slides.length - 1
            ? this.currentIndex + 1
            : 0;
        this.goToSlide(newIndex);
    }
    goToSlide(index) {
        if (index < 0 || index >= this.slides.length)
            return;
        // Update slides
        this.slides.forEach((slide, i) => {
            slide.classList.toggle('is-active', i === index);
        });
        // Update thumbnails
        this.thumbnails.forEach((thumb, i) => {
            thumb.classList.toggle('is-active', i === index);
        });
        // Scroll the active thumbnail into view so the user can always see the
        // currently-selected image in the capped (5-tall) thumb rail.
        const activeThumb = this.thumbnails[index];
        if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
            activeThumb.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
        // Keep the "current / total" counter badge in sync
        if (this.counter) {
            this.counter.textContent = `${index + 1} / ${this.slides.length}`;
        }
        this.currentIndex = index;
        // Play the in-gallery video only while its slide is active; stop it otherwise.
        if (this.videoSlide) {
            if (index === this.videoIndex) {
                this.startGalleryVideo?.();
            }
            else {
                this.stopGalleryVideo?.();
            }
        }
    }
    getCurrentImage() {
        const activeSlide = this.slides[this.currentIndex];
        return activeSlide?.querySelector('img') || null;
    }
    /**
     * When the thumb rail overflows its 5-thumb cap, append a sticky "+N more"
     * chip so buyers can tell there are additional images. Clicking it scrolls
     * the rail; it hides once the rail is scrolled to the end.
     */
    initThumbOverflowChip() {
        const strip = this.container.querySelector('[data-gallery-thumbs]');
        if (!strip || strip.scrollHeight <= strip.clientHeight + 8)
            return;
        const thumbSize = (this.thumbnails[0]?.offsetHeight || 80) + 8;
        const hidden = Math.max(Math.round((strip.scrollHeight - strip.clientHeight) / thumbSize), 1);
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'mm-pdp-gallery__more-chip';
        chip.textContent = `+${hidden} more`;
        chip.setAttribute('aria-label', `Scroll to see ${hidden} more images`);
        chip.addEventListener('click', () => {
            strip.scrollBy({ top: strip.clientHeight, behavior: 'smooth' });
        });
        strip.appendChild(chip);
        strip.addEventListener('scroll', () => {
            const atEnd = strip.scrollTop + strip.clientHeight >= strip.scrollHeight - 8;
            chip.style.display = atEnd ? 'none' : '';
        }, { passive: true });
    }
    initZoom() {
        this.slides.forEach((slide) => {
            if (slide.hasAttribute('data-video-slide'))
                return;
            const img = slide.querySelector('img');
            if (!img)
                return;
            img.addEventListener('mouseenter', () => this.showZoom(img));
            img.addEventListener('mouseleave', () => this.hideZoom());
            img.addEventListener('mousemove', (e) => this.updateZoom(e, img));
        });
    }
    showZoom(img) {
        if (!this.zoomContainer)
            return;
        const zoomSrc = img.getAttribute('data-zoom-image') || img.src;
        this.zoomContainer.style.backgroundImage = `url(${zoomSrc})`;
        this.zoomContainer.classList.add('is-active');
    }
    hideZoom() {
        this.zoomContainer?.classList.remove('is-active');
    }
    updateZoom(e, img) {
        if (!this.zoomContainer)
            return;
        const rect = img.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        this.zoomContainer.style.backgroundPosition = `${x}% ${y}%`;
    }
    initLightbox() {
        this.slides.forEach((slide) => {
            if (slide.hasAttribute('data-video-slide'))
                return;
            const img = slide.querySelector('img');
            if (!img)
                return;
            img.addEventListener('click', () => {
                this.openLightbox();
            });
            // Change cursor to indicate clickable
            img.style.cursor = 'zoom-in';
        });
    }
    /**
     * In-gallery YouTube video (first slide). Uses a lightweight thumbnail facade
     * and only injects the iframe once the gallery scrolls into view, so the page
     * isn't slowed by loading the YouTube player up front. Autoplays muted +
     * looping; the "Tap for sound" button (a user gesture) reloads it unmuted.
     */
    initGalleryVideo() {
        if (!this.videoSlide)
            return;
        const videoId = this.videoSlide.getAttribute('data-video-id');
        const wrap = this.videoSlide.querySelector('[data-gallery-video]');
        if (!videoId || !wrap)
            return;
        const playBtn = wrap.querySelector('[data-video-play]');
        const soundBtn = wrap.querySelector('[data-video-unmute]');
        const title = this.videoSlide.querySelector('img')?.getAttribute('alt') || 'Product video';
        const buildSrc = (muted) => `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${muted ? 1 : 0}` +
            `&loop=1&playlist=${videoId}&controls=1&rel=0&modestbranding=1&playsinline=1` +
            `&origin=${encodeURIComponent(location.origin)}`;
        const load = (muted) => {
            let iframe = wrap.querySelector('iframe');
            if (!iframe) {
                iframe = document.createElement('iframe');
                iframe.className = 'pdp-gallery-video__iframe';
                iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
                iframe.setAttribute('allowfullscreen', '');
                iframe.setAttribute('title', title);
                // The site sends Referrer-Policy: no-referrer site-wide; without a referrer
                // YouTube's player rejects the embed with "Error 153". An element-level
                // referrerpolicy overrides the document policy for just this iframe so the
                // origin is sent and YouTube can verify the embedding domain.
                iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
                wrap.appendChild(iframe);
            }
            iframe.src = buildSrc(muted);
            wrap.classList.add('is-playing');
            if (soundBtn)
                soundBtn.hidden = !muted; // only offer "tap for sound" while muted
            this.videoLoaded = true;
        };
        const stop = () => {
            const iframe = wrap.querySelector('iframe');
            if (iframe)
                iframe.remove();
            wrap.classList.remove('is-playing');
            if (soundBtn)
                soundBtn.hidden = true;
            this.videoLoaded = false;
        };
        this.startGalleryVideo = () => {
            if (!this.videoLoaded)
                load(true);
        };
        this.stopGalleryVideo = stop;
        // Manual play / unmute — a click is a user gesture, so sound is allowed.
        playBtn?.addEventListener('click', () => load(false));
        soundBtn?.addEventListener('click', () => load(false));
        if ('IntersectionObserver' in window) {
            const io = new IntersectionObserver((entries) => {
                const entry = entries[0];
                if (!entry)
                    return;
                if (entry.isIntersecting && this.currentIndex === this.videoIndex) {
                    if (!this.videoLoaded)
                        load(true);
                }
                else if (!entry.isIntersecting) {
                    stop();
                }
            }, { threshold: 0.5 });
            io.observe(this.videoSlide);
        }
        else {
            // No IntersectionObserver: just autoplay muted right away.
            load(true);
        }
    }
    openLightbox() {
        const currentImage = this.getCurrentImage();
        if (!currentImage)
            return;
        // Create lightbox overlay
        const overlay = document.createElement('div');
        overlay.className = 'lightbox-overlay';
        overlay.innerHTML = `
      <div class="lightbox-content">
        <button class="lightbox-close" type="button" aria-label="Close">&times;</button>
        <button class="lightbox-prev" type="button" aria-label="Previous">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <img src="${currentImage.getAttribute('data-zoom-image') || currentImage.src}" alt="${currentImage.alt || ''}">
        <button class="lightbox-next" type="button" aria-label="Next">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
        <div class="lightbox-thumbnails"></div>
      </div>
    `;
        // Add thumbnails
        const thumbContainer = overlay.querySelector('.lightbox-thumbnails');
        const lightboxImg = overlay.querySelector('.lightbox-content img');
        this.thumbnails.forEach((thumb, index) => {
            const img = document.createElement('img');
            const imageUrl = thumb.getAttribute('data-image-url') || '';
            const zoomUrl = thumb.getAttribute('data-zoom-url') || imageUrl;
            img.src = imageUrl;
            img.className = index === this.currentIndex ? 'is-active' : '';
            img.addEventListener('click', () => {
                if (lightboxImg) {
                    lightboxImg.src = zoomUrl;
                }
                thumbContainer?.querySelectorAll('img').forEach((t, i) => {
                    t.classList.toggle('is-active', i === index);
                });
                this.goToSlide(index);
            });
            thumbContainer?.appendChild(img);
        });
        // Navigation handlers
        let lightboxIndex = this.currentIndex;
        const updateLightbox = (newIndex) => {
            if (newIndex < 0)
                newIndex = this.slides.length - 1;
            if (newIndex >= this.slides.length)
                newIndex = 0;
            lightboxIndex = newIndex;
            const thumb = this.thumbnails[newIndex];
            const zoomUrl = thumb?.getAttribute('data-zoom-url') || thumb?.getAttribute('data-image-url') || '';
            if (lightboxImg && zoomUrl) {
                lightboxImg.src = zoomUrl;
            }
            thumbContainer?.querySelectorAll('img').forEach((t, i) => {
                t.classList.toggle('is-active', i === newIndex);
            });
            this.goToSlide(newIndex);
        };
        overlay.querySelector('.lightbox-prev')?.addEventListener('click', (e) => {
            e.stopPropagation();
            updateLightbox(lightboxIndex - 1);
        });
        overlay.querySelector('.lightbox-next')?.addEventListener('click', (e) => {
            e.stopPropagation();
            updateLightbox(lightboxIndex + 1);
        });
        // Close handlers
        overlay.querySelector('.lightbox-close')?.addEventListener('click', () => {
            overlay.remove();
        });
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                overlay.remove();
            }
        });
        // Keyboard navigation in lightbox
        const handleKeydown = (e) => {
            if (e.key === 'Escape') {
                overlay.remove();
                document.removeEventListener('keydown', handleKeydown);
            }
            else if (e.key === 'ArrowLeft') {
                updateLightbox(lightboxIndex - 1);
            }
            else if (e.key === 'ArrowRight') {
                updateLightbox(lightboxIndex + 1);
            }
        };
        document.addEventListener('keydown', handleKeydown);
        document.body.appendChild(overlay);
    }
}
export default ProductGallery;
